/**
 * 错误提示优化组件
 * 
 * P2问题修复：优化错误提示信息
 */

/* eslint-disable react-refresh/only-export-components -- 与 ErrorBoundary 同文件导出枚举与工具配置 */

import React from 'react';
import { Result, Button, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { logger } from '../utils/logger';

const { Paragraph, Text } = Typography;

/**
 * 错误边界组件
 */
class ErrorBoundaryClass extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '48px', textAlign: 'center' }}>
          <Result
            status="error"
            title="页面出错了"
            subTitle="抱歉，页面遇到了一些问题"
            extra={[
              <Button type="primary" key="reload" onClick={() => window.location.reload()}>
                刷新页面
              </Button>,
            ]}
          />
        </div>
      );
    }

    return this.props.children;
  }
}

export const ErrorBoundary = ErrorBoundaryClass;

/**
 * 错误类型枚举
 */
export enum ErrorType {
  NETWORK = 'network',
  NOT_FOUND = 'not_found',
  PERMISSION = 'permission',
  SERVER = 'server',
  BUSINESS = 'business',
}

/**
 * 错误配置
 */
const ERROR_CONFIG = {
  [ErrorType.NETWORK]: {
    status: 'error',
    title: '网络错误',
    subTitle: '无法连接到服务器，请检查网络连接',
    icon: '🔌',
  },
  [ErrorType.NOT_FOUND]: {
    status: '404',
    title: '页面不存在',
    subTitle: '您访问的页面不存在或已被删除',
    icon: '🔍',
  },
  [ErrorType.PERMISSION]: {
    status: '403',
    title: '没有权限',
    subTitle: '您没有权限访问此页面或执行此操作',
    icon: '🔒',
  },
  [ErrorType.SERVER]: {
    status: '500',
    title: '服务器错误',
    subTitle: '服务器内部错误，请稍后重试',
    icon: '⚠️',
  },
  [ErrorType.BUSINESS]: {
    status: 'warning',
    title: '业务错误',
    subTitle: '操作失败，请检查输入信息',
    icon: '❌',
  },
};

/**
 * 错误页面组件
 */
interface ErrorPageProps {
  type: ErrorType;
  message?: string;
  detail?: string;
  showRetry?: boolean;
  showHome?: boolean;
  onRetry?: () => void;
}

export const ErrorPage: React.FC<ErrorPageProps> = ({
  type,
  message,
  detail,
  showRetry = true,
  showHome = true,
  onRetry,
}) => {
  const navigate = useNavigate();
  const config = ERROR_CONFIG[type];

  return (
    <div style={{ 
      padding: '48px', 
      textAlign: 'center',
      minHeight: '400px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Result
        status={config.status as '403' | '404' | '500' | 'error' | 'info' | 'success' | 'warning'}
        icon={<span style={{ fontSize: '72px' }}>{config.icon}</span>}
        title={message || config.title}
        subTitle={detail || config.subTitle}
        extra={[
          showRetry && onRetry && (
            <Button type="primary" key="retry" onClick={onRetry}>
              重试
            </Button>
          ),
          showHome && (
            <Button key="home" onClick={() => navigate('/')}>
              返回首页
            </Button>
          ),
        ].filter(Boolean)}
      >
        {detail && (
          <Paragraph>
            <Text type="secondary" style={{ fontSize: '14px' }}>
              错误详情：{detail}
            </Text>
          </Paragraph>
        )}
      </Result>
    </div>
  );
};

/**
 * 空状态组件
 */
interface EmptyStateProps {
  type?: 'default' | 'search' | 'data' | 'permission';
  title?: string;
  description?: string;
  action?: React.ReactNode;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  type = 'default',
  title,
  description,
  action,
}) => {
  const EMPTY_CONFIG = {
    default: {
      icon: '📭',
      title: '暂无数据',
      description: '这里还没有任何数据',
    },
    search: {
      icon: '🔍',
      title: '未找到结果',
      description: '没有找到符合条件的数据，请调整筛选条件',
    },
    data: {
      icon: '📊',
      title: '暂无数据',
      description: '还没有相关数据，点击按钮添加',
    },
    permission: {
      icon: '🔒',
      title: '无权限',
      description: '您没有权限查看此数据',
    },
  };

  const config = EMPTY_CONFIG[type];

  return (
    <div style={{ 
      padding: '48px', 
      textAlign: 'center',
      color: '#999'
    }}>
      <div style={{ fontSize: '64px', marginBottom: '16px' }}>
        {config.icon}
      </div>
      <div style={{ fontSize: '16px', marginBottom: '8px', color: '#333' }}>
        {title || config.title}
      </div>
      <div style={{ fontSize: '14px', marginBottom: '16px' }}>
        {description || config.description}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
};

/**
 * 加载状态组件
 */
interface LoadingStateProps {
  tip?: string;
  fullScreen?: boolean;
}

export const LoadingState: React.FC<LoadingStateProps> = ({
  tip = '加载中...',
  fullScreen = false,
}) => {
  const style: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px',
  };

  if (fullScreen) {
    style.minHeight = '100vh';
    style.position = 'fixed';
    style.top = 0;
    style.left = 0;
    style.right = 0;
    style.bottom = 0;
    style.background = 'rgba(255, 255, 255, 0.8)';
    style.zIndex = 9999;
  }

  return (
    <div style={style}>
      <div style={{ textAlign: 'center' }}>
        <div className="ant-spin-dot ant-spin-dot-spin" style={{ fontSize: '32px' }}>
          <i className="ant-spin-dot-item" />
          <i className="ant-spin-dot-item" />
          <i className="ant-spin-dot-item" />
        </div>
        <div style={{ marginTop: '16px', color: '#666' }}>{tip}</div>
      </div>
    </div>
  );
};

/**
 * 确认对话框配置
 */
export const CONFIRM_CONFIG = {
  delete: {
    title: '确认删除',
    content: '删除后数据将无法恢复，确定要删除吗？',
    okText: '确定删除',
    okType: 'danger' as const,
    cancelText: '取消',
  },
  batchDelete: {
    title: '批量删除确认',
    content: '确定要删除选中的数据吗？删除后将无法恢复。',
    okText: '确定删除',
    okType: 'danger' as const,
    cancelText: '取消',
  },
  submit: {
    title: '确认提交',
    content: '确定要提交吗？',
    okText: '确定',
    okType: 'primary' as const,
    cancelText: '取消',
  },
  cancel: {
    title: '确认取消',
    content: '取消后未保存的数据将丢失，确定要取消吗？',
    okText: '确定',
    okType: 'default' as const,
    cancelText: '继续编辑',
  },
  transfer: {
    title: '确认转让',
    content: '确定要将此客户转让给其他人吗？',
    okText: '确定转让',
    okType: 'primary' as const,
    cancelText: '取消',
  },
};

/**
 * 错误消息映射
 */
export const ERROR_MESSAGES: Record<string, string> = {
  // 网络错误
  'Network Error': '网络连接失败，请检查网络',
  'timeout of 30000ms exceeded': '请求超时，请稍后重试',
  'Request failed with status code 404': '请求的资源不存在',
  'Request failed with status code 500': '服务器内部错误',
  
  // 业务错误
  '客户名称已存在': '客户名称已存在，请使用其他名称',
  '没有权限': '您没有权限执行此操作',
  '数据不存在': '数据不存在或已被删除',
  '参数错误': '请求参数错误，请检查输入',
  
  // 默认错误
  'default': '操作失败，请稍后重试',
};

/**
 * 获取友好的错误消息
 */
export const getErrorMessage = (error: unknown): string => {
  if (!error) return ERROR_MESSAGES.default;
  
  const errObj = error as Record<string, unknown>;
  const message = (typeof errObj.message === 'string' ? errObj.message : '') || (typeof errObj.msg === 'string' ? errObj.msg : '') || String(error);
  
  // 查找匹配的错误消息
  for (const [key, value] of Object.entries(ERROR_MESSAGES)) {
    if (message.includes(key)) {
      return value;
    }
  }
  
  return message || ERROR_MESSAGES.default;
};

// 默认导出 ErrorBoundary 类组件
export default ErrorBoundaryClass;
